import React, { useState, useEffect, useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { SimpleSelectField, TrackerContext } from '@unite-us/app-components';
import {
  showProgramStatusToggle,
  hasPaymentsUserAccess,
  includePathwaysServices,
  pays6694RemoveContextPerson,
} from 'common/utils/FeatureFlags/flags';
import callOrLog from 'common/utils/callOrLog';
import { REFERRAL } from 'common/utils/EventTracker/utils/eventConstants';
import { Serializer } from '@unite-us/client-utils';
import { fetchCorePrograms, fetchCoreFeeScheduleProgram } from 'src/actions/Group/Network';

const programButtonClass = (programField = {}, programs = []) => classNames({
  'referral-program-select__program-wrapper': true,
  'referral-program-select__program-wrapper--empty': _.isEmpty(programs),
  hide: !_.isEmpty(programField.value),
  'fade-in': _.isEmpty(programField.value),
});

const programSelectClass = (programField = {}) => classNames({
  'referral-program__select-field': true,
  hide: _.isEmpty(programField.value),
  'fade-in': !_.isEmpty(programField.value),
});

async function populateFeeScheduleProgram(program) {
  const fspId = _.get(program, 'relationships.fee_schedule_program.data.id', '');
  const fspData = await fetchCoreFeeScheduleProgram(fspId);
  const { relationships, ...restProgram } = program;
  const augmentedProgram = {
    ...restProgram,
    relationships: {
      ...relationships,
      fee_schedule_program: {
        ...relationships.fee_schedule_program,
        data: {
          ...relationships.fee_schedule_program.data,
          authorization_required: fspData?.attributes?.authorization_required || false,
        },
      },
    },
  };
  return augmentedProgram;
}

const ReferralProgramSelect = (props) => {
  const {
    contact,
    employeeId,
    excludeAuthorizationRequired,
    feeScheduleProgramId,
    includePathways,
    index,
    onProgramSelect: onProgramSelectProp,
    pays6694RemoveContextPersonFlag,
    programField,
    selectedGroupId,
    serviceType,
    usePaymentsUserRole,
    useReferralToggle: receiving_referrals,
  } = props;

  const [programs, setPrograms] = useState([]);
  const eventTracker = useContext(TrackerContext);

  const onTrackProgram = useCallback((program) => {
    if (program) {
      const eventPayload = Serializer.build({ contact });
      callOrLog(() => eventTracker(REFERRAL.programSelected, { contact: eventPayload }));
    }
  }, [contact, eventTracker]);

  const onProgramSelect = useCallback((program) => {
    onTrackProgram(program);
    onProgramSelectProp(program, index);
  }, [index, onTrackProgram, onProgramSelectProp]);

  const initProgramField = useCallback(() => {
    programField.onChange({ id: '' });
  }, [programField]);

  useEffect(() => {
    const fetchProgramOptions = async () => {
      const eventPayload = Serializer.build({ contact });
      const referable = { referable: { person: contact.id, employee: employeeId } };
      const filterByFspId = feeScheduleProgramId ? { feeScheduleProgramId } : {};

      const fetchedPrograms = await fetchCorePrograms({
        ...(pays6694RemoveContextPersonFlag ? {} : { clientId: contact.id }),
        provider: selectedGroupId,
        receiving_referrals,
        services: serviceType.id,
        usePaymentsUserRole,
        excludeAuthorizationRequired,
        includePathways,
        ...referable,
        ...filterByFspId,
      });

      if (usePaymentsUserRole) {
        const augmentedProgramPromises = fetchedPrograms.map(async (program) => {
          if (_.get(program, 'relationships.fee_schedule_program.data')) {
            const augmentedProgram = await populateFeeScheduleProgram(program);
            return {
              ...augmentedProgram,
              attributes: {
                ...augmentedProgram.attributes,
                name: `${augmentedProgram.attributes.name} (Contracted Program)`,
              },
            };
          }
          return program;
        });
        const augmentedPrograms = await Promise.all(augmentedProgramPromises);
        setPrograms(augmentedPrograms);
      } else {
        setPrograms(fetchedPrograms);
      }

      callOrLog(() => eventTracker(REFERRAL.selectProgram, { contact: eventPayload }));
    };

    fetchProgramOptions();
  }, []); // eslint-disable-line

  const autoSelect = !!feeScheduleProgramId;
  const displayOptions = autoSelect && programs ? programs.slice(0, 1) : programs;
  const error = programField.touched && !programField.value?.id ? 'Required' : null;

  return (
    <div className="referral-program-select">
      <div className={programButtonClass(programField, programs)}>
        {
          _.isEmpty(programs) ? <div /> : (
            <a
              className={
                classNames(
                  'mt-one',
                  `referral-program-selected__add-program-${index}`,
                  { 'text-red': programField.touched && !programField.valid },
                )
              }
              role="button"
              onClick={initProgramField}
              tabIndex="0"
            >
              Select Program
            </a>
          )
        }
      </div>
      <SimpleSelectField
        id={`select-field-program-${index}`}
        className={programSelectClass(programField)}
        label="program-select"
        hideLabel
        value={programField.value}
        onChange={onProgramSelect}
        options={displayOptions}
        optionSettings={{ labelKey: 'attributes.name', valueKey: 'id' }}
        required
        error={error}
        searchable
      />
    </div>
  );
};

ReferralProgramSelect.propTypes = {
  contact: PropTypes.object,
  employeeId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  programField: PropTypes.object.isRequired,
  selectedGroupId: PropTypes.string.isRequired,
  serviceType: PropTypes.object.isRequired,
  usePaymentsUserRole: PropTypes.bool.isRequired,
  useReferralToggle: PropTypes.bool.isRequired,
  excludeAuthorizationRequired: PropTypes.bool,
  includePathways: PropTypes.bool.isRequired,
  onProgramSelect: PropTypes.func.isRequired,
  feeScheduleProgramId: PropTypes.string,
  pays6694RemoveContextPersonFlag: PropTypes.bool.isRequired,
};

ReferralProgramSelect.defaultProps = {
  contact: {},
  feeScheduleProgramId: null,
  excludeAuthorizationRequired: false,
};

const mapStateToProps = (state) => {
  const usePaymentsUserRole = hasPaymentsUserAccess(state);
  const useReferralToggle = showProgramStatusToggle(state);
  const employeeId = _.get(state, 'globalState.currentEmployee.id');
  const includePathways = includePathwaysServices(state);
  const pays6694RemoveContextPersonFlag = pays6694RemoveContextPerson(state);

  return {
    employeeId,
    usePaymentsUserRole,
    useReferralToggle,
    includePathways,
    pays6694RemoveContextPersonFlag,
  };
};

export default connect(mapStateToProps)(ReferralProgramSelect);
